import React from "react";
import { motion } from "framer-motion";
import { Search, Plus, Download, ChevronLeft, ChevronRight, MoreHorizontal, Loader2, Upload, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { EmptyState, SkeletonRows } from "@/components/dash/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

export default function DataTable({ config }) {
  const { user } = useAuth();
  const [data, setData] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [q, setQ] = React.useState("");
  const [status, setStatus] = React.useState("all");
  const [detail, setDetail] = React.useState(null);
  const [showCreate, setShowCreate] = React.useState(false);
  const [selected, setSelected] = React.useState(new Set());
  const [bulkPick, setBulkPick] = React.useState(null); // {action}
  const fileRef = React.useRef(null);

  const bulkActions = config.bulkActions ? config.bulkActions(user.role) : [];
  const hasBulk = bulkActions.length > 0 || config.bulkUpload;

  const load = React.useCallback(async () => {
    setData(null); setSelected(new Set());
    try {
      const params = { page, page_size: 10, ...(config.params || {}) };
      if (q) params.q = q;
      if (status !== "all" && config.statuses) params.status = status;
      const { data } = await api.get(config.endpoint, { params });
      setData(data);
    } catch (e) { setData({ items: [], total: 0, pages: 1 }); }
  }, [page, q, status, config.endpoint, JSON.stringify(config.params)]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { setPage(1); }, [q, status]);

  const canCreate = config.create && (!config.create.roles || config.create.roles.includes(user.role));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = data?.items?.length > 0 && data.items.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected((s) => allOn ? new Set() : new Set(data.items.map((r) => r.id)));

  const runBulk = async (action, extra) => {
    const ids = [...selected];
    try { await action.run(ids, extra); toast.success(action.toast || `${ids.length} records updated`); setBulkPick(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const onBulkClick = (action) => { if (action.pickRequirement) setBulkPick({ action }); else runBulk(action); };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(Boolean);
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const rows = lines.slice(1).map((ln) => {
      const cells = ln.split(","); const o = {}; headers.forEach((h, i) => (o[h] = (cells[i] || "").trim())); return o;
    });
    try { const { data } = await api.post("/candidates/bulk", { candidates: rows }); toast.success(`${data.created} added${data.duplicates?.length ? `, ${data.duplicates.length} duplicates skipped` : ""}`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const exportCsv = () => {
    const rows = data?.items || []; if (!rows.length) return;
    const cols = config.columns.filter((c) => c.key);
    const csv = [cols.map((c) => c.label).join(","), ...rows.map((r) => cols.map((c) => `"${String(r[c.key] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${config.key}.csv`; a.click(); URL.revokeObjectURL(url); toast.success("Export ready");
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={config.searchPlaceholder || "Search…"} className="pl-9" data-testid={`${config.key}-search`} />
        </div>
        {config.statuses && (
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full sm:w-44 bg-white" data-testid={`${config.key}-status-filter`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">All statuses</SelectItem>
              {config.statuses.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <div className="flex gap-2 sm:ml-auto">
          {config.bulkUpload && <>
            <input ref={fileRef} type="file" accept=".csv" onChange={uploadCsv} className="hidden" data-testid={`${config.key}-csv-input`} />
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid={`${config.key}-bulk-upload`}><Upload className="h-4 w-4 mr-1.5" />Bulk Upload</Button>
          </>}
          <Button variant="outline" onClick={exportCsv} data-testid={`${config.key}-export`}><Download className="h-4 w-4 mr-1.5" />Export</Button>
          {canCreate && <Button onClick={() => setShowCreate(true)} className="bg-vs-royal text-white" data-testid={`${config.key}-create`}><Plus className="h-4 w-4 mr-1.5" />{config.create.label || "New"}</Button>}
        </div>
      </div>

      {selected.size > 0 && bulkActions.length > 0 && (
        <div className="flex items-center gap-3 mb-3 rounded-xl bg-vs-light border border-blue-100 px-4 py-2.5" data-testid={`${config.key}-bulk-bar`}>
          <span className="text-sm font-medium text-vs-deep">{selected.size} selected</span>
          <div className="flex flex-wrap gap-2 ml-auto">
            {bulkActions.map((a) => (
              <Button key={a.label} size="sm" variant={a.danger ? "outline" : "default"} onClick={() => onBulkClick(a)}
                className={a.danger ? "text-rose-600 border-rose-200" : "bg-vs-royal text-white"} data-testid={`${config.key}-bulk-${a.label.replace(/\s/g,'-').toLowerCase()}`}>{a.label}</Button>
            ))}
            <button onClick={() => setSelected(new Set())} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      <div className="vs-card overflow-hidden">
        {data === null ? <div className="p-5"><SkeletonRows /></div> : data.items.length === 0 ? (
          <EmptyState icon={config.emptyIcon || "Inbox"} title={config.emptyTitle || "No records yet"} description={config.emptyDesc}
            action={canCreate && <Button onClick={() => setShowCreate(true)} className="bg-vs-royal text-white"><Plus className="h-4 w-4 mr-1.5" />{config.create.label || "Create"}</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/80">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                  {hasBulk && bulkActions.length > 0 && <th className="px-4 py-3 w-10"><Checkbox checked={allOn} onCheckedChange={toggleAll} data-testid={`${config.key}-select-all`} /></th>}
                  {config.columns.map((c) => <th key={c.label} className="px-4 py-3 font-semibold whitespace-nowrap">{c.label}</th>)}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {data.items.map((row, i) => (
                  <motion.tr key={row.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                    onClick={() => setDetail(row)} data-testid={`${config.key}-row-${row.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer">
                    {hasBulk && bulkActions.length > 0 && <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(row.id)} onCheckedChange={() => toggle(row.id)} data-testid={`${config.key}-select-${row.id}`} /></td>}
                    {config.columns.map((c) => (
                      <td key={c.label} className="px-4 py-3 whitespace-nowrap">
                        {c.render ? c.render(row) : c.status ? <StatusBadge status={row[c.key]} /> : <span className="text-slate-700">{row[c.key] ?? "—"}</span>}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <RowActions row={row} config={config} user={user} reload={load} onView={() => setDetail(row)} />
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <span className="text-xs text-slate-500">Page {data.page} of {data.pages} · {data.total} records</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${config.key}-prev`}><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid={`${config.key}-next`}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </div>

      <DetailSheet row={detail} config={config} user={user} onClose={() => setDetail(null)} reload={load} />
      {showCreate && <CreateModal config={config} onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />}
      {bulkPick && <RequirementPicker onClose={() => setBulkPick(null)} onPick={(rid) => runBulk(bulkPick.action, rid)} count={selected.size} />}
    </div>
  );
}

function RowActions({ row, config, user, reload, onView }) {
  const actions = config.actions ? config.actions(row, user.role) : [];
  const run = async (a) => {
    try {
      if (a.navigate) { window.location.assign(a.navigate(row)); return; }
      if (a.custom) await a.custom(row); else await api.patch(`${config.endpoint}/${row.id}`, a.patch);
      toast.success(a.toast || "Updated"); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><button className="p-1.5 rounded-md hover:bg-slate-100" data-testid={`${config.key}-actions-${row.id}`}><MoreHorizontal className="h-4 w-4 text-slate-500" /></button></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white w-52">
        <DropdownMenuItem onClick={onView} data-testid={`${config.key}-view-${row.id}`}>View details</DropdownMenuItem>
        {actions.map((a) => (
          <DropdownMenuItem key={a.label} onClick={() => run(a)} className={a.danger ? "text-rose-600" : ""} data-testid={`${config.key}-action-${a.label.replace(/\s/g,'-').toLowerCase()}-${row.id}`}>{a.label}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DetailSheet({ row, config, user, onClose, reload }) {
  if (!row) return null;
  const fields = config.detailFields || Object.keys(row).filter((k) => !["id", "_id", "org_id"].includes(k));
  const actions = config.actions ? config.actions(row, user.role) : [];
  const run = async (a) => {
    try {
      if (a.navigate) { window.location.assign(a.navigate(row)); return; }
      if (a.custom) await a.custom(row); else await api.patch(`${config.endpoint}/${row.id}`, a.patch);
      toast.success(a.toast || "Updated"); onClose(); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Sheet open={!!row} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-white w-full sm:max-w-md overflow-y-auto" data-testid={`${config.key}-detail`}>
        <SheetHeader><SheetTitle className="font-display">{config.detailTitle ? config.detailTitle(row) : (row.title || row.name || row.candidate_name || row.po_number || row.invoice_number || "Details")}</SheetTitle></SheetHeader>
        <div className="mt-6 space-y-4">
          {fields.map((f) => {
            const key = typeof f === "string" ? f : f.key;
            const label = typeof f === "string" ? key.replace(/_/g, " ") : f.label;
            let val = row[key];
            if (typeof f === "object" && f.render) return <div key={key}><div className="text-xs uppercase tracking-wide text-slate-400 mb-1">{label}</div><div className="text-sm text-slate-800">{f.render(row)}</div></div>;
            if (Array.isArray(val)) val = val.length ? val.join(", ") : "—";
            if (key.includes("status") || key.includes("verification")) return <div key={key}><div className="text-xs uppercase tracking-wide text-slate-400 mb-1">{label}</div><StatusBadge status={row[key]} /></div>;
            if (val == null || val === "") val = "—";
            if (typeof val === "object") val = JSON.stringify(val);
            return <div key={key}><div className="text-xs uppercase tracking-wide text-slate-400 mb-1 capitalize">{label}</div><div className="text-sm text-slate-800 break-words">{String(val)}</div></div>;
          })}
        </div>
        {actions.length > 0 && (
          <div className="mt-8 pt-6 border-t border-slate-100 flex flex-wrap gap-2">
            {actions.map((a) => (
              <Button key={a.label} onClick={() => run(a)} variant={a.danger ? "outline" : "default"}
                className={a.danger ? "text-rose-600 border-rose-200" : "bg-vs-royal text-white"} data-testid={`${config.key}-detail-action-${a.label.replace(/\s/g,'-').toLowerCase()}`}>{a.label}</Button>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function CreateModal({ config, onClose, onDone }) {
  const [form, setForm] = React.useState(() => { const init = {}; config.create.fields.forEach((f) => { init[f.name] = f.default ?? ""; }); return init; });
  const [loading, setLoading] = React.useState(false);
  const [asyncOpts, setAsyncOpts] = React.useState({});
  React.useEffect(() => {
    config.create.fields.filter((f) => f.type === "select-async").forEach(async (f) => {
      try { const { data } = await api.get(f.endpoint, { params: { page_size: 100, ...(f.params || {}) } });
        setAsyncOpts((o) => ({ ...o, [f.name]: (data.items || []).map((it) => ({ value: it.id, label: f.optionLabel(it) })) })); } catch {}
    });
  }, []);
  const set = (name, val) => setForm((f) => ({ ...f, [name]: val }));
  const submit = async () => {
    for (const f of config.create.fields) if (f.required && !form[f.name]) return toast.error(`${f.label} is required`);
    setLoading(true);
    try {
      const payload = { ...form };
      config.create.fields.forEach((f) => {
        if (f.type === "tags" && typeof payload[f.name] === "string") payload[f.name] = payload[f.name].split(",").map((s) => s.trim()).filter(Boolean);
        if (f.type === "number" && payload[f.name] !== "") payload[f.name] = Number(payload[f.name]);
      });
      if (config.create.transform) Object.assign(payload, config.create.transform(payload));
      await api.post(config.create.endpoint || config.endpoint, payload);
      toast.success(`${config.create.label || "Record"} created`); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`${config.key}-create-modal`}>
        <DialogHeader><DialogTitle className="font-display">{config.create.title || `New ${config.create.label}`}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {config.create.fields.map((f) => (
            <div key={f.name}>
              <Label>{f.label}{f.required && <span className="text-rose-500">*</span>}</Label>
              {f.type === "textarea" ? <Textarea value={form[f.name]} onChange={(e) => set(f.name, e.target.value)} className="mt-1.5" data-testid={`field-${f.name}`} />
                : f.type === "select" ? (
                  <Select value={form[f.name]} onValueChange={(v) => set(f.name, v)}><SelectTrigger className="mt-1.5 bg-white" data-testid={`field-${f.name}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent className="bg-white">{f.options.map((o) => <SelectItem key={o} value={o} className="capitalize">{o}</SelectItem>)}</SelectContent></Select>
                ) : f.type === "select-async" ? (
                  <Select value={form[f.name]} onValueChange={(v) => set(f.name, v)}><SelectTrigger className="mt-1.5 bg-white" data-testid={`field-${f.name}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">{(asyncOpts[f.name] || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
                ) : <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={form[f.name]} onChange={(e) => set(f.name, e.target.value)} className="mt-1.5" placeholder={f.placeholder} data-testid={`field-${f.name}`} />}
              {f.hint && <p className="text-xs text-slate-400 mt-1">{f.hint}</p>}
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="bg-vs-royal text-white" data-testid={`${config.key}-submit`}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequirementPicker({ onClose, onPick, count }) {
  const [reqs, setReqs] = React.useState([]);
  const [val, setVal] = React.useState("");
  React.useEffect(() => { api.get("/requirements", { params: { page_size: 100 } }).then((r) => setReqs(r.data.items)).catch(() => {}); }, []);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="bulk-requirement-picker">
        <DialogHeader><DialogTitle className="font-display">Submit {count} candidate(s)</DialogTitle></DialogHeader>
        <div className="py-2"><Label>Requirement</Label>
          <Select value={val} onValueChange={setVal}><SelectTrigger className="mt-1.5 bg-white" data-testid="bulk-req-select"><SelectValue placeholder="Select requirement…" /></SelectTrigger>
            <SelectContent className="bg-white max-h-64">{reqs.map((r) => <SelectItem key={r.id} value={r.id}>{r.title} — {r.client_name}</SelectItem>)}</SelectContent></Select></div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!val} onClick={() => onPick(val)} className="bg-vs-royal text-white" data-testid="bulk-req-confirm">Submit Candidates</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
